// Pure form helpers for email templates (US-002, B1-8).
// The per-type subject collapse and delay/chronoUnit computation extracted from
// EmailTemplateForm.jsx handleSubmit, so BOTH the legacy individual form path and the
// aggregator map builder use the same single source of truth.
import CONSTANTS from "../../constants";
import { getDefaultSubject } from "../defaultSubjects";
import { entityToFormValuesMapper } from "../../../Common/utils";

// Backend email-template DTO shapes. Defined here (application) rather than in infrastructure so
// the helpers don't import upward into infrastructure; the API layer imports these from here.
export interface EmailTemplateTranslationDTO {
  locale: string;
  id?: number;
  subject?: string;
  body?: string;
  header?: string;
  footer?: string;
}

export interface EmailTemplateDTO {
  id?: number;
  hotelTicker: string;
  type: string;
  emailDestinationType: string;
  isActive: boolean;
  delay?: number | string;
  chronoUnit?: string;
  emailGuestType?: string;
  singleMailOnMultipleRoomStays?: boolean;
  translations?: EmailTemplateTranslationDTO[];
  preferred?: boolean;
  parentPropertyTicker?: string;
  parentId?: number;
  childPropertyTickers?: string[];
  isLinkedToAggregator?: boolean;
  descriptor?: string;
}

export type AgrTemplateMap = Record<string, EmailTemplateDTO>;

const TYPES = CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES;
const TEMPLATE_TYPES = CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES;
const CHRONO_UNITS = CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS;
const DESTINATION_TYPES = CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES;

// The per-type subject field a form translation carries (one per email type that owns a body).
type SubjectFieldName =
  | "cartAbandonmentSubject"
  | "paymentFailedSubject"
  | "abandonedPaymentSubject"
  | "upsellSubject"
  | "quotationSubject"
  | "waitingListSubject"
  | "welcomeLoyaltySubject"
  | "agenciesSubject";

export interface EmailTemplateTranslation extends Partial<Record<SubjectFieldName, string>> {
  locale: string;
  id?: number;
  subject?: string;
  body?: string;
  header?: string;
  footer?: string;
}

// `_isChain` / `_isAgrInIndividual` are transient flags the form injects into the values bag to
// drive field visibility (chain property selector, `preferred`). They never reach the DTO.
export interface EmailTemplateFormValues {
  id?: number | string;
  hotelTicker?: string;
  type?: string;
  templateType?: string;
  chronoUnit?: string;
  delay?: number | string;
  delayCheckout?: number | string;
  delayCheckin?: number | string;
  delayCartAbandonment?: number | string;
  emailDestinationType?: string;
  emailGuestType?: string;
  isActive?: boolean;
  singleMailOnMultipleRoomStays?: boolean;
  descriptor?: string;
  preferred?: boolean;
  translations?: EmailTemplateTranslation[];
  selectedMotors?: string[];
  _isChain?: boolean;
  _isAgrInIndividual?: boolean;
}

export function isCartAbandonmentType(type?: string): boolean {
  return type === TYPES.CART_ABANDONMENT;
}

function isReservationConfirmationType(type?: string): boolean {
  return type === TYPES.RESERVATION_CONFIRMATION;
}

const SUBJECT_TYPE_MAP: Record<string, string> = {
  [TYPES.CART_ABANDONMENT]: "cartAbandonment",
  [TYPES.RESERVATION_STATUS_RED]: "paymentFailed",
  [TYPES.RESERVATION_STATUS_ORANGE]: "abandonedPayment",
  [TYPES.UPSELL_AVAILABLE]: "upsell",
  [TYPES.QUOTATION_PRICE]: "quotation",
  [TYPES.WAITING_LIST_AVAILABLE]: "waitingList",
  [TYPES.LOYALTY_WELCOME]: "loyaltyWelcome",
  [TYPES.LOYALTY_WELCOME_VALIDATED]: "agencies",
};

const SUBJECT_FIELD_MAP: Record<string, SubjectFieldName> = {
  [TYPES.CART_ABANDONMENT]: "cartAbandonmentSubject",
  [TYPES.RESERVATION_STATUS_RED]: "paymentFailedSubject",
  [TYPES.RESERVATION_STATUS_ORANGE]: "abandonedPaymentSubject",
  [TYPES.UPSELL_AVAILABLE]: "upsellSubject",
  [TYPES.QUOTATION_PRICE]: "quotationSubject",
  [TYPES.WAITING_LIST_AVAILABLE]: "waitingListSubject",
  [TYPES.LOYALTY_WELCOME]: "welcomeLoyaltySubject",
  [TYPES.LOYALTY_WELCOME_VALIDATED]: "agenciesSubject",
};

const TYPES_WITH_BODY_TEMPLATE = new Set<string>([
  TYPES.CART_ABANDONMENT,
  TYPES.RESERVATION_STATUS_RED,
  TYPES.RESERVATION_STATUS_ORANGE,
  TYPES.UPSELL_AVAILABLE,
  TYPES.QUOTATION_PRICE,
  TYPES.WAITING_LIST_AVAILABLE,
  TYPES.LOYALTY_WELCOME,
  TYPES.LOYALTY_WELCOME_VALIDATED,
]);

export function resolveDelay(formValues: EmailTemplateFormValues): number {
  const calculators: Record<string, () => number> = {
    [TYPES.POST_STAY]: () => Number(formValues.delayCheckout) * 24 * 60,
    [TYPES.PRE_STAY]: () => Number(formValues.delayCheckin) * 24 * 60,
    [TYPES.CART_ABANDONMENT]: () => Number(formValues.delayCartAbandonment),
  };
  const calc = formValues.type ? calculators[formValues.type] : undefined;
  return calc ? calc() : Number(formValues.delay);
}

export function resolveChronoUnit(formValues: EmailTemplateFormValues): string | undefined {
  switch (formValues.type) {
    case TYPES.POST_STAY:
    case TYPES.PRE_STAY:
    case TYPES.CART_ABANDONMENT:
      return CHRONO_UNITS.MINUTES;
    default:
      return formValues.chronoUnit;
  }
}

export function resolveDestinationType(formValues: EmailTemplateFormValues): string {
  const guestOnlyTypes = new Set<string>([
    TYPES.CART_ABANDONMENT,
    TYPES.UPSELL_AVAILABLE,
    TYPES.QUOTATION_PRICE,
    TYPES.WAITING_LIST_AVAILABLE,
    TYPES.LOYALTY_WELCOME,
    TYPES.LOYALTY_WELCOME_VALIDATED,
  ]);
  return formValues.type && guestOnlyTypes.has(formValues.type)
    ? DESTINATION_TYPES.GUEST
    : (formValues.emailDestinationType as string);
}

export function resolveSingleMail(formValues: EmailTemplateFormValues): boolean {
  return isReservationConfirmationType(formValues.type)
    ? !!formValues.singleMailOnMultipleRoomStays
    : false;
}

export function isUseStandardTemplate(formValues: EmailTemplateFormValues): boolean {
  return formValues.templateType === TEMPLATE_TYPES.DEFAULT;
}

// Types whose content is body-based (vs header/footer wrapper). NOTE: this set includes
// RESERVATION_CONFIRMATION (unlike TYPES_WITH_BODY_TEMPLATE above, used for submission), because
// a confirmation IS Custom when it has a body. Used to derive Default/Custom for display.
const BODY_TEMPLATE_TYPES_FOR_DERIVATION = new Set<string>([
  TYPES.RESERVATION_CONFIRMATION,
  TYPES.CART_ABANDONMENT,
  TYPES.UPSELL_AVAILABLE,
  TYPES.QUOTATION_PRICE,
  TYPES.WAITING_LIST_AVAILABLE,
  TYPES.LOYALTY_WELCOME,
  TYPES.LOYALTY_WELCOME_VALIDATED,
  TYPES.RESERVATION_STATUS_RED,
  TYPES.RESERVATION_STATUS_ORANGE,
]);

// Derives Default vs Custom from the template's translations (single source for the form's
// entity→form mapping and the list's "Template type" column). Body-based types are Custom when
// a translation has a body; wrapper types are Custom when header/footer is missing.
export function resolveTemplateType(values: EmailTemplateFormValues): string {
  const translations = values.translations || [];
  if (values.type && BODY_TEMPLATE_TYPES_FOR_DERIVATION.has(values.type)) {
    return translations.some(t => t.body) ? TEMPLATE_TYPES.CUSTOM : TEMPLATE_TYPES.DEFAULT;
  }
  return !translations.some(t => t.header) || !translations.some(t => t.footer)
    ? TEMPLATE_TYPES.CUSTOM
    : TEMPLATE_TYPES.DEFAULT;
}

export function buildTranslations(formValues: EmailTemplateFormValues): EmailTemplateTranslation[] {
  const translations = formValues.translations || [];
  return translations.map(translation => {
    if (isUseStandardTemplate(formValues)) {
      const result: EmailTemplateTranslation = { locale: translation.locale };
      if (translation.id) {
        result.id = translation.id;
      }
      if (formValues.type && TYPES_WITH_BODY_TEMPLATE.has(formValues.type)) {
        const subjectType = SUBJECT_TYPE_MAP[formValues.type];
        if (subjectType) {
          result.subject =
            translation.subject || getDefaultSubject(subjectType, translation.locale);
        }
      } else {
        result.header = translation.header;
        result.footer = translation.footer;
      }
      return result;
    }

    const subjectField = formValues.type ? SUBJECT_FIELD_MAP[formValues.type] : undefined;
    const subject = subjectField ? translation[subjectField] : translation.subject;
    return { locale: translation.locale, subject, body: translation.body };
  });
}

export function normalizeEmailTemplate(formValues: EmailTemplateFormValues): EmailTemplateDTO {
  const entity: EmailTemplateDTO = {
    hotelTicker: formValues.hotelTicker as string,
    type: formValues.type as string,
    delay: resolveDelay(formValues),
    chronoUnit: resolveChronoUnit(formValues),
    emailDestinationType: resolveDestinationType(formValues),
    isActive: !!formValues.isActive,
    singleMailOnMultipleRoomStays: resolveSingleMail(formValues),
    translations: buildTranslations(formValues),
  };
  if (isCartAbandonmentType(formValues.type) && !!formValues.emailGuestType) {
    entity.emailGuestType = formValues.emailGuestType;
  }
  if (formValues.id) {
    entity.id = Number(formValues.id);
  }
  // `descriptor` is required by the backend DTO. The form has no descriptor input, so synthesize a
  // stable one (type_destination_ticker) when absent — same shape the aggregator path uses.
  entity.descriptor =
    formValues.descriptor || `${entity.type}_${entity.emailDestinationType}_${entity.hotelTicker}`;
  // Carry `preferred` through when set so the full DTO for a shared child template reaches
  // the legacy PUT (backend applies only `preferred` on a linked template, B3-5).
  if (formValues.preferred !== undefined) {
    entity.preferred = formValues.preferred;
  }
  return entity;
}

// Link to the parent template's edit screen on the aggregator engine, shown in the
// read-only banner of a shared template on a child engine (mirrors Conditions/CancellationPolicy).
export function buildAgrReadonlyLink(
  parentPropertyTicker: string,
  parentId: number | string,
  entityName: string
): string {
  return `/${parentPropertyTicker}/${entityName}/${parentId}`;
}

// Builds the aggregator map { parentTicker: DTO, ...childTicker: DTO } expected by the
// aggregator API. Each entry's hotelTicker must equal its map key. Children carry
// parentPropertyTicker (parentId is set by backend) and never carry the parent's id.
export function buildAgrTemplateMap(
  dto: EmailTemplateDTO,
  parentTicker: string,
  selectedMotors: string[]
): AgrTemplateMap {
  const map: AgrTemplateMap = {
    [parentTicker]: { ...dto, hotelTicker: parentTicker },
  };
  selectedMotors.forEach(childTicker => {
    const child: EmailTemplateDTO = {
      ...dto,
      hotelTicker: childTicker,
      parentPropertyTicker: parentTicker,
    };
    delete child.id;
    map[childTicker] = child;
  });
  return map;
}

// PRE_STAY/POST_STAY store the delay in days in the form, but in minutes in the entity.
const MINUTES_PER_DAY = 24 * 60;

// Maps a persisted email template entity into the form's values: derives the per-type delay
// units and Default/Custom template type, and collapses the type-specific subject into both its
// own field and the generic `subject`. Single source shared by the form's entityToFormValuesMapper.
export function mapEntityToEmailTemplateFormValues(params: {
  entity: { active?: boolean };
  fields: unknown;
  locales: unknown;
  translationConfig: unknown;
}): EmailTemplateFormValues {
  const formValues: EmailTemplateFormValues = entityToFormValuesMapper(params);

  const delayInDays = formValues.type === TYPES.PRE_STAY || formValues.type === TYPES.POST_STAY;
  const delayMultiplier = delayInDays ? 1 / MINUTES_PER_DAY : 1;
  formValues.delayCheckout = Number(formValues.delay) * delayMultiplier;
  formValues.delayCheckin = Number(formValues.delay) * delayMultiplier;
  formValues.templateType = resolveTemplateType(formValues);

  const subjectField = formValues.type ? SUBJECT_FIELD_MAP[formValues.type] : undefined;
  const subjectType = formValues.type ? SUBJECT_TYPE_MAP[formValues.type] : undefined;
  if (subjectField && subjectType) {
    const useDefaultSubject = isUseStandardTemplate(formValues);
    formValues.translations = (formValues.translations || []).map(translation => {
      const shouldUseDefault = !translation.body || useDefaultSubject;
      const subject = shouldUseDefault
        ? getDefaultSubject(subjectType, translation.locale)
        : translation[subjectField] || translation.subject;
      return { ...translation, [subjectField]: subject, subject };
    });
    if (isCartAbandonmentType(formValues.type)) {
      formValues.delayCartAbandonment =
        formValues.delay ||
        formValues.delayCartAbandonment ||
        CONSTANTS.ENTITY.CART_ABANDONMENT.defaultDelay;
    }
  }

  formValues.isActive = params.entity.active;
  return formValues;
}
